/**
 * F-506-FE-05: ActiveSessionPage
 *
 * Full-screen consultation view at /session/:id.
 * THE critical page — zero distractions, transcript is the hero.
 *
 * Features:
 * - Live bidirectional transcript via WebSocket (SessionContext segments)
 * - Confidence indicators with color AND icon (WCAG: never color alone)
 *   - Green checkmark  : >85%
 *   - Amber warning    : 60–85%
 *   - Red alert circle : <60%
 * - Header: language pair, session duration timer
 * - Safety flag banners:
 *   - RED: "Translation held — review required" with Confirm/Reject
 *   - AMBER: warning text, non-blocking
 * - Session controls at bottom (away from transcript scroll area):
 *   - End Session: 56px target, confirmation dialog
 *   - Escalate: 56px target
 * - Headset status for doctor and patient
 * - Volume control
 * - TTS direction indicator
 *
 * RTL support via CSS logical properties.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/contexts/SessionContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useHeadsetDetection } from '@/features/session/hooks/useHeadsetDetection';
import { useAudioCapture } from '@/features/session/hooks/useAudioCapture';
import { useVAD } from '@/features/session/hooks/useVAD';
import { useTTSPlayback } from '@/features/session/hooks/useTTSPlayback';
import { ConnectionStatus } from '@/components/ConnectionStatus';
import { InterimBubble } from './InterimBubble';
import { StreamErrorToast } from './StreamErrorToast';
import { api } from '@/lib/api';
import type { TranscriptSegment, HeldSafetyItem } from '@/contexts/SessionContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SessionDetail {
  id: string;
  status: string;
  doctorLanguageCode: string;
  patientLanguageCode: string;
  templateVersionId: string;
  createdAt: string;
  sttMode?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getLanguageName(code: string): string {
  try {
    return new Intl.DisplayNames([navigator.language || 'en'], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

// ---------------------------------------------------------------------------
// Confidence indicator (color AND icon — WCAG)
// ---------------------------------------------------------------------------

interface ConfidenceIndicatorProps {
  confidence: number;
  size?: 'sm' | 'md';
}

function ConfidenceIndicator({ confidence, size = 'sm' }: ConfidenceIndicatorProps) {
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  if (confidence > 85) {
    return (
      <span className="inline-flex items-center gap-1" aria-label={`Translation confidence: high (${confidence}%)`}>
        <svg className={`${iconSize} text-safety-green flex-shrink-0`} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
        </svg>
        <span className="sr-only">High confidence</span>
      </span>
    );
  }

  if (confidence >= 60) {
    return (
      <span className="inline-flex items-center gap-1" aria-label={`Translation confidence: moderate (${confidence}%)`}>
        <svg className={`${iconSize} text-safety-amber flex-shrink-0`} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </svg>
        <span className="sr-only">Moderate confidence</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" aria-label={`Translation confidence: low (${confidence}%)`}>
      <svg className={`${iconSize} text-safety-red flex-shrink-0`} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm4.22-2.78a.75.75 0 0 1 1.06 0L8 7.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 9l2.72 2.72a.75.75 0 0 1-1.06 1.06L8 10.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 9 4.22 6.28a.75.75 0 0 1 0-1.06Z" />
      </svg>
      <span className="sr-only">Low confidence</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Transcript segment
// ---------------------------------------------------------------------------

interface SegmentProps {
  segment: TranscriptSegment;
}

function SegmentBubble({ segment }: SegmentProps) {
  const isDoctor = segment.direction === 'doctor_to_patient';
  // Derive confidence from safetyFlags (use safetyLevel as proxy)
  const confidenceValue =
    segment.safetyLevel === 'green' ? 90 :
    segment.safetyLevel === 'amber' ? 72 : 45;

  return (
    <div
      className={`flex flex-col gap-1 ${isDoctor ? 'items-end' : 'items-start'}`}
      aria-label={`${isDoctor ? 'Doctor' : 'Patient'} said: ${isDoctor ? segment.originalText : segment.translatedText}`}
    >
      {/* Speaker label */}
      <div className={`flex items-center gap-1.5 ${isDoctor ? 'flex-row-reverse' : ''}`}>
        <span className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide">
          {isDoctor ? 'Doctor' : 'Patient'}
        </span>
        <ConfidenceIndicator confidence={confidenceValue} />
      </div>

      {/* Bubble — doctor always sees their own language (English) as primary */}
      <div
        className={`max-w-[85%] px-4 py-3 rounded-clinical text-sm font-body leading-relaxed shadow-clinical ${
          isDoctor
            ? 'bg-clinical-charcoal text-white rounded-br-sm'
            : 'bg-white text-clinical-charcoal-soft border border-clinical-border rounded-bl-sm'
        }`}
      >
        <p>{isDoctor ? segment.originalText : segment.translatedText}</p>
        {(() => {
          const secondaryText = isDoctor ? segment.translatedText : segment.originalText;
          const secondaryLabel = isDoctor ? 'Translation' : 'Original';
          const primaryText = isDoctor ? segment.originalText : segment.translatedText;
          if (!secondaryText || secondaryText === primaryText) return null;
          return (
            <p className={`text-xs mt-1.5 italic ${isDoctor ? 'text-white/60' : 'text-clinical-text-muted'}`}>
              {secondaryLabel}: {secondaryText}
            </p>
          );
        })()}
      </div>

      {/* Correction indicator — shown when LLM corrected this segment's translation */}
      {segment.correctedText && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-1.5 text-xs font-body text-blue-600"
          aria-label="Translation corrected"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Zm1.414 1.06a.25.25 0 0 0-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 0 0 0-.354l-1.086-1.086ZM11.189 6.25 9.75 4.81l-6.286 6.287a.25.25 0 0 0-.064.108l-.558 1.953 1.953-.558a.25.25 0 0 0 .108-.064l6.286-6.286Z" />
          </svg>
          <span>Corrected translation</span>
        </div>
      )}

      {/* Amber safety flag — non-blocking inline warning */}
      {segment.safetyLevel === 'amber' && segment.safetyFlags.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-1.5 text-xs font-body text-amber-600"
          aria-label="Translation advisory"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
          </svg>
          Advisory: {segment.safetyFlags.join(', ')}
        </div>
      )}

      {/* Degradation indicator — shown when AI translation was unavailable */}
      {segment.degradationLevel && segment.degradationLevel !== 'none' && (
        <div
          role="status"
          className="flex items-center gap-1.5 text-xs font-body text-amber-500"
          aria-label={segment.degradationLevel === 'translation' ? 'Machine translated' : 'Untranslated transcript'}
          title={segment.degradationLevel === 'translation' ? 'Machine translated (AI unavailable)' : 'Untranslated transcript'}
        >
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" aria-hidden="true" />
          {segment.degradationLevel === 'translation' ? 'Machine translated' : 'Untranslated'}
        </div>
      )}

      {/* Incomplete utterance indicator — patient paused mid-thought (patient-to-doctor only) */}
      {segment.incomplete && !isDoctor && (
        <span className="text-xs text-clinical-text-muted italic ml-1">[patient paused]</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Held safety item banner (RED — requires confirm/reject)
// ---------------------------------------------------------------------------

interface HeldBannerProps {
  item: HeldSafetyItem;
  onConfirm: (segmentId: string) => void;
  onReject: (segmentId: string) => void;
}

function HeldSafetyBanner({ item, onConfirm, onReject }: HeldBannerProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="bg-red-50 border border-red-300 rounded-clinical p-4 space-y-3"
    >
      <div className="flex items-start gap-2">
        <svg
          className="w-5 h-5 flex-shrink-0 mt-0.5 text-safety-red"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm4.22-2.78a.75.75 0 0 1 1.06 0L8 7.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 9l2.72 2.72a.75.75 0 0 1-1.06 1.06L8 10.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 9 4.22 6.28a.75.75 0 0 1 0-1.06Z" />
        </svg>
        <div>
          <p className="text-sm font-body font-semibold text-red-800">
            Translation held — review required
          </p>
          <p className="text-xs font-body text-red-700 mt-1">
            {item.translatedText}
          </p>
          {item.flags.length > 0 && (
            <p className="text-xs font-body text-red-600 mt-1">
              Flags: {item.flags.join(', ')}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => onConfirm(item.segmentId)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5
                     bg-red-700 text-white rounded-clinical text-sm font-body font-medium
                     hover:bg-red-800 transition-colors
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1
                     min-h-[44px]"
          aria-label="Confirm and deliver this translation to patient"
        >
          Confirm and send
        </button>
        <button
          onClick={() => onReject(item.segmentId)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5
                     bg-white text-red-700 border border-red-300 rounded-clinical text-sm font-body font-medium
                     hover:bg-red-50 transition-colors
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1
                     min-h-[44px]"
          aria-label="Reject this translation"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// End Session confirmation dialog
// ---------------------------------------------------------------------------

interface EndSessionDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function EndSessionDialog({ isOpen, onConfirm, onCancel }: EndSessionDialogProps) {
  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-session-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-clinical-charcoal/50"
        onClick={onCancel}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div className="relative bg-white rounded-clinical shadow-clinical-lg p-6 w-full max-w-sm">
        <h2
          id="end-session-dialog-title"
          className="font-heading text-xl font-semibold text-clinical-charcoal mb-2"
        >
          End session?
        </h2>
        <p className="text-sm font-body text-clinical-charcoal-soft mb-6">
          This will end the consultation. A clinical note will be generated automatically.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="btn-secondary flex-1"
          >
            Keep going
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5
                       bg-safety-red text-white rounded-clinical font-body font-medium text-sm
                       hover:bg-red-600 transition-colors
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            style={{ minHeight: 'var(--touch-target-critical)' }}
            aria-label="Confirm end session"
          >
            End session
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headset status pill
// ---------------------------------------------------------------------------

interface HeadsetPillProps {
  label: string;
  connected: boolean;
}

function HeadsetPill({ label, connected }: HeadsetPillProps) {
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-xs font-body text-white/80"
      aria-label={`${label} headset: ${connected ? 'connected' : 'not detected'}`}
    >
      <svg
        className={`w-3 h-3 flex-shrink-0 ${connected ? 'text-safety-green' : 'text-white/40'}`}
        fill="currentColor"
        viewBox="0 0 16 16"
        aria-hidden="true"
      >
        {connected ? (
          <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
        ) : (
          <circle cx="8" cy="8" r="4.5" fillOpacity="0.3" />
        )}
      </svg>
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ActiveSessionPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    segments,
    heldSafetyItems,
    headsetStatus,
    vadState,
    pipelineMode,
    setSession,
    appendSegment,
    updateSegment,
    holdSafetyItem,
    clearHeldSafetyItem,
    setHeadsetStatus,
    setVadState,
  } = useSession();
  const { send, onMessage, rawSocket, isReconnecting, justReconnected, setSessionId, status: wsStatus } = useWebSocket();
  const { setDirectionFromLanguage } = useTheme();
  const queryClient = useQueryClient();

  const { headsetConnected: doctorHeadset } = useHeadsetDetection();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [volume, setVolume] = useState(80);
  // F-603: Track whether session ended while disconnected
  const [sessionEndedWhileDisconnected, setSessionEndedWhileDisconnected] = useState(false);
  // Pairing data for QR/6-digit code display during PAIRING state
  const [pairingData, setPairingData] = useState<{ qrToken: string; sixDigitCode: string; expiresIn: number } | null>(null);
  // STT streaming interim state
  const [interimText, setInterimText] = useState<string | null>(null);
  const [interimIsFinal, setInterimIsFinal] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  // Full stream mode — patient direction interim + processing states
  const [patientInterimText, setPatientInterimText] = useState<string | null>(null);
  const [patientInterimIsFinal, setPatientInterimIsFinal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [patientIsProcessing, setPatientIsProcessing] = useState(false);
  // System alerts — shown when pipeline cannot deliver patient speech
  const [systemAlertMessage, setSystemAlertMessage] = useState<string | null>(null);
  const systemAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Speech drop alert — shown when a patient segment could not be transcribed
  const [speechDropAlert, setSpeechDropAlert] = useState<{ message: string } | null>(null);
  const speechDropTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Translation pending indicator — in-flight pipeline chunks after TTS playback ends
  const [translationPending, setTranslationPending] = useState(false);

  const transcriptBottomRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch session details
  // ---------------------------------------------------------------------------
  const { data: sessionDetail } = useQuery<SessionDetail>({
    queryKey: ['session', sessionId],
    queryFn: async () => {
      const res = await api.get<{ data: SessionDetail }>(`/api/sessions/${sessionId ?? ''}`);
      return res.data;
    },
    enabled: Boolean(sessionId),
    // Poll while waiting for patient to join — stops automatically once ACTIVE.
    // This is the primary mechanism for detecting the PAIRING→ACTIVE transition.
    // The WS-based SESSION_STATE invalidation (below) provides faster updates
    // when the WebSocket chain is working, but polling is the reliable fallback.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return !status || status === 'PAIRING' ? 3000 : false;
    },
  });

  // ---------------------------------------------------------------------------
  // Wire up session in context on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (sessionDetail) {
      setSession({
        sessionId: sessionDetail.id,
        status: sessionDetail.status as never,
        doctorLanguageCode: sessionDetail.doctorLanguageCode,
        patientLanguageCode: sessionDetail.patientLanguageCode,
        templateVersionId: sessionDetail.templateVersionId,
        pipelineMode: (sessionDetail.sttMode as 'batch' | 'streaming' | 'full_stream' | null | undefined) ?? null,
      });
    }
  }, [sessionDetail, setSession]);

  // ---------------------------------------------------------------------------
  // FE-03: RTL direction — set document dir from patient language (WCAG)
  // Doctor interface is always LTR; patient language (e.g. Arabic) governs RTL.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (sessionDetail?.patientLanguageCode) {
      setDirectionFromLanguage(sessionDetail.patientLanguageCode);
    }
    return () => {
      // Reset to LTR when leaving the session page
      setDirectionFromLanguage('en');
    };
  }, [sessionDetail?.patientLanguageCode, setDirectionFromLanguage]);

  // ---------------------------------------------------------------------------
  // F-603: Register active sessionId with WebSocketContext for SESSION_REJOIN
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (sessionId) {
      setSessionId(sessionId);
    }
    return () => {
      setSessionId(null);
    };
  }, [sessionId, setSessionId]);

  // ---------------------------------------------------------------------------
  // Send JOIN_SESSION when WebSocket is connected and sessionId is known.
  // This associates the doctor's WS connection with the session in Redis
  // (sets doctorWsId), enabling SESSION_STATE broadcasts to reach this client.
  // ---------------------------------------------------------------------------
  const joinSentRef = useRef(false);
  useEffect(() => {
    if (wsStatus === 'connected' && sessionId && !joinSentRef.current) {
      joinSentRef.current = true;
      send({ type: 'JOIN_SESSION', sessionId });
    }
  }, [wsStatus, sessionId, send]);

  // ---------------------------------------------------------------------------
  // Read pairing data from sessionStorage (set by PreSessionSetup)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (sessionId) {
      const stored = sessionStorage.getItem(`session_qr_${sessionId}`);
      if (stored) {
        try {
          setPairingData(JSON.parse(stored) as { qrToken: string; sixDigitCode: string; expiresIn: number });
        } catch {
          // Corrupt data — ignore.
        }
      }
    }
  }, [sessionId]);

  // ---------------------------------------------------------------------------
  // Duration timer
  // ---------------------------------------------------------------------------
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1_000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Audio capture — sends binary PCM frames via raw WebSocket
  // ---------------------------------------------------------------------------
  const {
    startCapture,
    stopCapture,
    isCapturing,
    error: audioError,
    analyserRef,
  } = useAudioCapture({ sessionId: sessionId ?? '', ws: rawSocket });

  // VAD — voice activity detection using the same audio context analyser
  const { pauseVAD, resumeVAD, isSpeaking } = useVAD({
    ws: rawSocket,
    analyserNode: analyserRef.current,
  });

  // TTS playback — receives binary TTS frames from server
  const { clearQueue: clearTTSQueue } = useTTSPlayback({ ws: rawSocket, pauseVAD, resumeVAD });

  // When doctor starts speaking, flush any queued patient TTS clips immediately
  // so the doctor's speech takes priority per existing interlock.
  useEffect(() => {
    if (isSpeaking) {
      clearTTSQueue();
    }
  }, [isSpeaking, clearTTSQueue]);

  // Auto-start audio capture when session becomes ACTIVE and socket is ready
  useEffect(() => {
    if (sessionDetail?.status === 'ACTIVE' && rawSocket && !isCapturing) {
      void startCapture();
    }
    return () => {
      if (isCapturing) stopCapture();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDetail?.status, rawSocket]);

  // ---------------------------------------------------------------------------
  // WebSocket message handlers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // New transcript segment
    unsubs.push(
      onMessage('segment', (msg) => {
        const segDirection = (msg.direction as 'doctor_to_patient' | 'patient_to_doctor') ?? 'doctor_to_patient';
        appendSegment({
          segmentId: String(msg.segmentId ?? ''),
          direction: segDirection,
          originalText: String(msg.originalText ?? ''),
          translatedText: String(msg.translatedText ?? ''),
          safetyLevel: (msg.safetyLevel as 'green' | 'amber' | 'red') ?? 'green',
          safetyFlags: Array.isArray(msg.safetyFlags) ? (msg.safetyFlags as string[]) : [],
          timestamp: String(msg.timestamp ?? new Date().toISOString()),
          degradationLevel: (msg.degradationLevel as 'none' | 'translation' | 'transcript_only') ?? 'none',
          ...(msg.incomplete ? { incomplete: true as const } : {}),
          seq: typeof msg.seq === 'number' ? msg.seq : undefined,
        });
        // Clear matching direction's interim + processing state
        if (segDirection === 'patient_to_doctor') {
          setPatientInterimText(null);
          setPatientInterimIsFinal(false);
          setPatientIsProcessing(false);
        } else {
          setInterimText(null);
          setInterimIsFinal(false);
          setIsProcessing(false);
        }
      }),
    );

    // Held TTS (RED flag)
    unsubs.push(
      onMessage('safety:held', (msg) => {
        holdSafetyItem({
          segmentId: String(msg.segmentId ?? ''),
          originalText: String(msg.originalText ?? ''),
          translatedText: String(msg.translatedText ?? ''),
          flags: Array.isArray(msg.flags) ? (msg.flags as string[]) : [],
          timestamp: String(msg.timestamp ?? new Date().toISOString()),
        });
      }),
    );

    // Headset status update from patient device
    unsubs.push(
      onMessage('headset:status', (msg) => {
        if (msg.device === 'patient') {
          setHeadsetStatus('patient', Boolean(msg.connected));
        }
      }),
    );

    // VAD state update
    unsubs.push(
      onMessage('vad:state', (msg) => {
        setVadState((msg.state as 'idle' | 'doctor_speaking' | 'patient_speaking' | 'tts_playing') ?? 'idle');
      }),
    );

    // F-603: SESSION_REJOINED — server confirms session is still active
    unsubs.push(
      onMessage('SESSION_REJOINED', (msg) => {
        // Update VAD state from server snapshot
        if (typeof msg.vadState === 'string') {
          setVadState(msg.vadState as 'idle' | 'doctor_speaking' | 'patient_speaking' | 'tts_playing');
        }
      }),
    );

    // F-603: SESSION_ENDED — server reports session is no longer active on rejoin
    unsubs.push(
      onMessage('SESSION_ENDED', (msg) => {
        if (msg.reason === 'SESSION_NOT_ACTIVE' || msg.reason === 'SESSION_NOT_FOUND') {
          setSessionEndedWhileDisconnected(true);
          // Navigate to session end page after brief delay
          setTimeout(() => {
            void navigate(`/session/${sessionId ?? ''}/end`);
          }, 2_000);
        }
      }),
    );

    // SESSION_STATE — broadcast by backend when doctor/patient joins WS session.
    // Invalidate the session query so the UI picks up the new status (e.g. ACTIVE).
    unsubs.push(
      onMessage('SESSION_STATE', () => {
        void queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
      }),
    );

    // STT interim transcript — route by direction for full_stream mode
    unsubs.push(
      onMessage('stt:interim', (msg) => {
        const dir = msg.direction as string | undefined;
        if (dir === 'patient_to_doctor') {
          setPatientInterimText(String(msg.text ?? ''));
          setPatientInterimIsFinal(Boolean(msg.isFinal));
        } else {
          setInterimText(String(msg.text ?? ''));
          setInterimIsFinal(Boolean(msg.isFinal));
        }
      }),
    );

    // Full stream: pause_pending — show processing state
    unsubs.push(
      onMessage('fullstream:pause_pending', (msg) => {
        const dir = msg.direction as string | undefined;
        if (dir === 'patient_to_doctor') {
          setPatientIsProcessing(true);
        } else {
          setIsProcessing(true);
        }
      }),
    );

    // Translation pending indicator — in-flight pipelines after TTS playback ends
    unsubs.push(
      onMessage('translation_pending', (msg) => {
        const count = typeof msg.inFlightCount === 'number' ? msg.inFlightCount : 0;
        setTranslationPending(count > 0);
      }),
    );

    // STT stream error — shown as amber toast
    unsubs.push(
      onMessage('stt:stream_error', (msg) => {
        setStreamError(String(msg.message ?? 'Streaming error — switching to batch mode'));
      }),
    );

    // System alerts — pipeline could not deliver patient speech (audio unclear, translation error, etc.)
    // The alert audio plays automatically via the binary TTS frame. This handler shows a brief
    // visual indicator alongside the audio for accessibility.
    unsubs.push(
      onMessage('system:alert', (msg) => {
        const message = String(msg.message ?? 'Pipeline alert');
        setSystemAlertMessage(message);
        // Clear any existing timer
        if (systemAlertTimerRef.current) clearTimeout(systemAlertTimerRef.current);
        systemAlertTimerRef.current = setTimeout(() => {
          setSystemAlertMessage(null);
          systemAlertTimerRef.current = null;
        }, 8000);
      }),
    );

    // transcript:correction — LLM detected a clinically significant error in a previous
    // chunk's translation and issued a correction. Update the segment in the transcript.
    unsubs.push(
      onMessage('transcript:correction', (msg) => {
        const segId = typeof msg.segmentId === 'string' ? msg.segmentId : null;
        const corrected = typeof msg.correctedText === 'string' ? msg.correctedText : null;
        if (segId && corrected) {
          updateSegment(segId, corrected);
        }
      }),
    );

    // pipeline:speech_drop — backend could not transcribe a patient segment despite real speech energy.
    // Show a brief inline alert so the doctor can prompt the patient to repeat.
    unsubs.push(
      onMessage('pipeline:speech_drop', (msg) => {
        const message = typeof msg.message === 'string'
          ? msg.message
          : 'A patient segment could not be transcribed. You may want to ask the patient to repeat.';
        setSpeechDropAlert({ message });
        if (speechDropTimerRef.current) clearTimeout(speechDropTimerRef.current);
        speechDropTimerRef.current = setTimeout(() => {
          setSpeechDropAlert(null);
          speechDropTimerRef.current = null;
        }, 10_000);
      }),
    );

    return () => {
      unsubs.forEach((u) => u());
      if (systemAlertTimerRef.current) clearTimeout(systemAlertTimerRef.current);
      if (speechDropTimerRef.current) clearTimeout(speechDropTimerRef.current);
    };
  }, [onMessage, appendSegment, updateSegment, holdSafetyItem, setHeadsetStatus, setVadState, navigate, sessionId, queryClient]);

  // Sync local doctor headset status
  useEffect(() => {
    setHeadsetStatus('doctor', doctorHeadset);
  }, [doctorHeadset, setHeadsetStatus]);

  // ---------------------------------------------------------------------------
  // Auto-scroll to latest segment or interim bubble
  // ---------------------------------------------------------------------------
  useEffect(() => {
    transcriptBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [segments, interimText]);

  // ---------------------------------------------------------------------------
  // Safety confirm / reject
  // ---------------------------------------------------------------------------
  const handleSafetyConfirm = useCallback(
    (segmentId: string) => {
      send({ type: 'safety:confirm', segmentId });
      clearHeldSafetyItem(segmentId);
    },
    [send, clearHeldSafetyItem],
  );

  const handleSafetyReject = useCallback(
    (segmentId: string) => {
      send({ type: 'safety:reject', segmentId });
      clearHeldSafetyItem(segmentId);
    },
    [send, clearHeldSafetyItem],
  );

  // ---------------------------------------------------------------------------
  // End session
  // ---------------------------------------------------------------------------
  async function handleEndSession() {
    if (!sessionId || isEnding) return;
    setIsEnding(true);
    try {
      await api.patch(`/api/sessions/${sessionId}/transition`, {
        targetStatus: 'ENDING',
      });
      navigate(`/session/${sessionId}/end`);
    } catch {
      setIsEnding(false);
      setShowEndDialog(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Escalate
  // ---------------------------------------------------------------------------
  async function handleEscalate() {
    if (!sessionId) return;
    try {
      await api.post(`/api/sessions/${sessionId}/escalate`, {});
    } catch {
      // Best-effort
    }
  }

  // ---------------------------------------------------------------------------
  // TTS direction indicator
  // ---------------------------------------------------------------------------
  function getTTSLabel(): string | null {
    if (vadState === 'tts_playing') {
      return 'Translating to patient...';
    }
    if (vadState === 'patient_speaking') {
      return 'Translating to you...';
    }
    if (translationPending) {
      return 'Translating...';
    }
    return null;
  }

  const ttsLabel = getTTSLabel();
  const doctorLang = getLanguageName(sessionDetail?.doctorLanguageCode ?? '');
  const patientLang = getLanguageName(sessionDetail?.patientLanguageCode ?? '');

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="fixed inset-0 flex flex-col bg-clinical-cream" aria-label="Active consultation session">
      {/* F-603: Connection status banner — non-blocking, appears at top */}
      <ConnectionStatus
        isReconnecting={isReconnecting}
        justReconnected={justReconnected}
        sessionEndedWhileDisconnected={sessionEndedWhileDisconnected}
      />

      {/* Header */}
      <header className="flex-shrink-0 bg-clinical-charcoal text-white px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          {/* Language pair */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-body font-semibold text-white">
              {doctorLang}
            </span>
            <svg className="w-4 h-4 text-white/50 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 3M21 7.5H7.5" />
            </svg>
            <span className="text-sm font-body font-semibold text-white">
              {patientLang}
            </span>
          </div>

          {/* Duration */}
          <div
            className="text-sm font-body text-white/80 tabular-nums"
            aria-label={`Session duration: ${formatDuration(elapsedSeconds)}`}
          >
            {formatDuration(elapsedSeconds)}
          </div>
        </div>

        {/* Headset status row */}
        <div className="max-w-2xl mx-auto flex items-center gap-2 mt-2">
          <HeadsetPill label="Your headset" connected={headsetStatus.doctor} />
          <HeadsetPill label="Patient headset" connected={headsetStatus.patient} />
          {pipelineMode && (
            <span className="text-xs font-body font-medium px-2 py-0.5 rounded-full bg-clinical-cream-dark text-clinical-charcoal">
              {pipelineMode === 'full_stream' ? 'Full Stream' : pipelineMode === 'streaming' ? 'Stream' : 'Batch'}
            </span>
          )}
        </div>
      </header>

      {/* TTS direction indicator */}
      {ttsLabel && (
        <div
          role="status"
          aria-live="polite"
          className="flex-shrink-0 bg-clinical-teal/10 border-b border-clinical-teal/20 px-4 py-2"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-clinical-teal animate-pulse" aria-hidden="true" />
            <span className="text-xs font-body font-medium text-clinical-teal">
              {ttsLabel}
            </span>
          </div>
        </div>
      )}

      {/* Audio capture error */}
      {audioError && (
        <div
          role="alert"
          className="flex-shrink-0 bg-red-50 border-b border-red-200 px-4 py-2"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0 text-safety-red" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm4.22-2.78a.75.75 0 0 1 1.06 0L8 7.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 9l2.72 2.72a.75.75 0 0 1-1.06 1.06L8 10.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 9 4.22 6.28a.75.75 0 0 1 0-1.06Z" />
            </svg>
            <span className="text-xs font-body font-medium text-red-700">
              {audioError}
            </span>
          </div>
        </div>
      )}

      {/* STT stream error toast */}
      <StreamErrorToast message={streamError} onDismiss={() => setStreamError(null)} />

      {/* System alert banner — shown when pipeline could not deliver patient speech */}
      {systemAlertMessage && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0 text-safety-amber animate-pulse" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
            </svg>
            <span className="text-xs font-body font-medium text-amber-700">
              {systemAlertMessage}
            </span>
            <button
              type="button"
              onClick={() => setSystemAlertMessage(null)}
              className="ms-auto text-amber-500 hover:text-amber-700 focus:outline-none"
              aria-label="Dismiss alert"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Speech drop alert — shown when a patient segment could not be transcribed */}
      {speechDropAlert && (
        <div
          role="alert"
          aria-live="polite"
          className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
            </svg>
            <span className="text-xs font-body font-medium text-amber-700">
              {speechDropAlert.message}
            </span>
            <button
              type="button"
              onClick={() => setSpeechDropAlert(null)}
              className="ms-auto text-amber-500 hover:text-amber-700 focus:outline-none"
              aria-label="Dismiss speech drop alert"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Held safety banners */}
      {heldSafetyItems.length > 0 && (
        <div className="flex-shrink-0 px-4 py-3 space-y-2">
          <div className="max-w-2xl mx-auto space-y-2">
            {heldSafetyItems.map((item) => (
              <HeldSafetyBanner
                key={item.segmentId}
                item={item}
                onConfirm={handleSafetyConfirm}
                onReject={handleSafetyReject}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pairing overlay — shown when session is in PAIRING state */}
      {sessionDetail?.status === 'PAIRING' && (
        <div
          className="flex-1 flex items-center justify-center px-4 py-8"
          aria-label="Waiting for patient to join"
        >
          <div className="max-w-sm w-full bg-white rounded-clinical shadow-clinical-lg p-8 text-center space-y-6">
            {/* Pairing icon */}
            <div className="w-16 h-16 rounded-full bg-clinical-teal/10 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-clinical-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
            </div>

            <div>
              <h2 className="font-heading text-xl font-semibold text-clinical-charcoal">
                Waiting for patient
              </h2>
              <p className="text-sm font-body text-clinical-charcoal-soft mt-1">
                Ask the patient to visit <span className="font-semibold">/join</span> on their device and enter this code:
              </p>
            </div>

            {/* 6-digit code — the primary pairing mechanism */}
            {pairingData ? (
              <div className="space-y-4">
                <div
                  className="text-5xl font-heading font-bold tracking-[0.3em] text-clinical-charcoal bg-clinical-cream rounded-clinical py-4 px-6 select-all"
                  aria-label={`Pairing code: ${pairingData.sixDigitCode.split('').join(' ')}`}
                >
                  {pairingData.sixDigitCode}
                </div>
                <p className="text-xs font-body text-clinical-text-muted">
                  Patient join URL:{' '}
                  <span className="font-mono text-clinical-charcoal-soft break-all select-all">
                    {window.location.origin}/join?sessionId={sessionId}&token={pairingData.qrToken}
                  </span>
                </p>
              </div>
            ) : (
              <div className="py-4">
                <span className="w-6 h-6 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin inline-block" aria-hidden="true" />
                <p className="text-xs font-body text-clinical-text-muted mt-2">
                  Loading pairing code...
                </p>
              </div>
            )}

            {/* Pulsing indicator */}
            <div className="flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-clinical-teal animate-pulse" aria-hidden="true" />
              <span className="text-xs font-body text-clinical-text-muted">
                Session will start automatically when patient connects
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Transcript — the hero element (hidden during PAIRING) */}
      {sessionDetail?.status !== 'PAIRING' && (
      <main
        className="flex-1 overflow-y-auto px-4 py-4"
        aria-label="Consultation transcript"
        aria-live="polite"
        aria-relevant="additions"
      >
        <div className="max-w-2xl mx-auto space-y-4">
          {segments.length === 0 && !interimText ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center">
              <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                </svg>
              </div>
              <p className="text-sm font-body text-clinical-text-muted">
                Waiting for speech...
              </p>
              <p className="text-xs font-body text-clinical-text-muted mt-1">
                Speak clearly. The system will translate automatically.
              </p>
            </div>
          ) : (
            <>
              {segments.map((seg) => (
                <SegmentBubble key={seg.segmentId} segment={seg} />
              ))}
              {patientInterimText && (
                <InterimBubble
                  text={patientInterimText}
                  isFinal={patientInterimIsFinal}
                  direction="patient_to_doctor"
                  isProcessing={patientIsProcessing}
                />
              )}
              {interimText && (
                <InterimBubble
                  text={interimText}
                  isFinal={interimIsFinal}
                  direction="doctor_to_patient"
                  isProcessing={isProcessing}
                />
              )}
            </>
          )}
          <div ref={transcriptBottomRef} aria-hidden="true" />
        </div>
      </main>
      )}

      {/* Bottom controls bar — positioned AWAY from transcript */}
      <footer className="flex-shrink-0 bg-white border-t border-clinical-border px-4 py-3 safe-area-inset-bottom">
        <div className="max-w-2xl mx-auto">
          {/* Volume control */}
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-4 h-4 text-clinical-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
            </svg>
            <input
              type="range"
              min={0}
              max={100}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full accent-clinical-teal"
              aria-label={`Volume: ${volume}%`}
            />
            <span className="text-xs font-body text-clinical-text-muted w-8 text-end tabular-nums">
              {volume}%
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            {/* Escalate */}
            <button
              onClick={() => void handleEscalate()}
              className="flex-1 inline-flex items-center justify-center gap-2
                         bg-amber-50 text-amber-700 border border-amber-200
                         rounded-clinical font-body font-semibold text-sm
                         hover:bg-amber-100 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              style={{ minHeight: 'var(--touch-target-critical)' }}
              aria-label="Escalate this session to a supervisor"
            >
              <svg className="w-4 h-4 flex-shrink-0 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
              </svg>
              Escalate
            </button>

            {/* End Session */}
            <button
              onClick={() => setShowEndDialog(true)}
              className="flex-1 inline-flex items-center justify-center gap-2
                         bg-safety-red text-white rounded-clinical font-body font-semibold text-sm
                         hover:bg-red-600 transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: 'var(--touch-target-critical)' }}
              disabled={isEnding}
              aria-label="End this consultation session"
            >
              {isEnding ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                  Ending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9" />
                  </svg>
                  End Session
                </>
              )}
            </button>
          </div>
        </div>
      </footer>

      {/* End session confirmation dialog */}
      <EndSessionDialog
        isOpen={showEndDialog}
        onConfirm={() => void handleEndSession()}
        onCancel={() => setShowEndDialog(false)}
      />
    </div>
  );
}
